import React from 'react';
import {StyleSheet, View} from 'react-native';
import {ThemeProvider} from '@rneui/themed';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';

import rewardsTheme from '../../../theme/rewardsTheme';
import Text from '../../themed/Text';

import AddToWallet from './index';

const meta = {
  title: 'Components/Rewards/Add To Wallet',
  component: AddToWallet,
  argTypes: {
    walletUrl: {
      control: 'text',
    },
    pending: {
      control: 'boolean',
    },
  },
  decorators: [
    Story => (
      <ThemeProvider theme={rewardsTheme}>
        <View style={styles.container}>
          <Story />
        </View>
      </ThemeProvider>
    ),
  ],
} satisfies Meta<typeof AddToWallet>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Default: Story = {
  args: {
    walletUrl: 'https://example.com/wallet',
    pending: false,
  },
};

export const Active: Story = {
  render: () => (
    <View style={styles.variantsContainer}>
      <Text variant="heading3">Add to Wallet</Text>
      <Text variant="body2" style={{marginBottom: 16}}>
        Click the button below to add your card to Apple Wallet or Google Wallet
      </Text>
      <AddToWallet walletUrl="https://example.com/wallet" pending={false} />
      <Text variant="caption" style={{marginTop: 8, textAlign: 'center'}}>
        The wallet button will appear based on your device (iOS = Apple Wallet, Android = Google
        Wallet)
      </Text>
    </View>
  ),
};

export const Pending: Story = {
  render: () => (
    <View style={styles.variantsContainer}>
      <Text variant="heading3">Add to Wallet (Pending)</Text>
      <Text variant="body2" style={{marginBottom: 16}}>
        Card is being processed. The button will be disabled until ready.
      </Text>
      <AddToWallet walletUrl="https://example.com/wallet" pending={true} />
    </View>
  ),
};

export const WithInteraction: Story = {
  render: () => (
    <View style={styles.variantsContainer}>
      <Text variant="heading3">Interactive Wallet Button</Text>
      <AddToWallet walletUrl="" pending={false} />
      <Text variant="caption" style={{marginTop: 8}}>
        (Note: Press the button to see interaction - in real app, would open wallet URL)
      </Text>
    </View>
  ),
};

export const AllVariants: Story = {
  render: () => (
    <View style={styles.variantsContainer}>
      <View>
        <Text variant="heading4">Active State</Text>
        <AddToWallet walletUrl="https://example.com/wallet" pending={false} />
      </View>

      <View style={{marginTop: 24}}>
        <Text variant="heading4">Pending State</Text>
        <AddToWallet walletUrl="https://example.com/wallet" pending={true} />
      </View>

      <View style={{marginTop: 24}}>
        <Text variant="caption" style={{textAlign: 'center'}}>
          Note: Button appearance varies by platform
          {'\n'}iOS: Apple Wallet button
          {'\n'}Android: Google Wallet button
          {'\n'}Web: Shows based on detected device
        </Text>
      </View>
    </View>
  ),
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    justifyContent: 'center',
  },
  variantsContainer: {
    gap: 8,
  },
});
